import Vue from 'vue';
import imageSegmentationApi from '@frontend/services/api/image-segmentation';

const STORAGE_KEY = 'psd-image-segmentation-jobs';
const MAX_ACTIVE_JOBS = 2;
const POLL_INTERVAL = 4000;

function restoreJobs() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch (error) {
    return {};
  }
}

export const segmentationState = Vue.observable({ jobs: restoreJobs() });
export const jobs = segmentationState.jobs;
let pollTimer = null;
let polling = false;

function saveJobs() {
  const saved = {};
  Object.entries(jobs).forEach(([path, job]) => {
    saved[path] = {
      imagePath: job.imagePath,
      psdFilePath: job.psdFilePath || '',
      customerId: job.customerId,
      jobUuid: job.jobUuid,
      status: job.status,
      progress: job.progress,
      pollCount: job.pollCount || 0,
      error: job.error || '',
    };
  });
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
  } catch (error) {
    // Storage is optional; jobs must still start when it is unavailable.
  }
}

function updateJob(path, changes) {
  Vue.set(jobs, path, { ...jobs[path], ...changes });
  saveJobs();
}

function activeCount() {
  return Object.values(jobs).filter(job => ['starting', 'waiting', 'processing'].includes(job.status)).length;
}

function startWaitingJobs() {
  Object.values(jobs)
    .filter(job => job.status === 'queued')
    .slice(0, Math.max(0, MAX_ACTIVE_JOBS - activeCount()))
    .forEach(job => startJob(job.imagePath));
}

async function startJob(path) {
  const job = jobs[path];
  if (!job || job.status !== 'queued') return;

  updateJob(path, { status: 'starting', progress: 0 });
  const startingJob = jobs[path];
  try {
    const { data } = await imageSegmentationApi.startSegmentation(job.customerId, path);
    if (jobs[path] !== startingJob) return;
    if (data.success === false || !data.job_uuid) {
      throw new Error(data.message || 'Could not start segmentation');
    }

    updateJob(path, {
      jobUuid: data.job_uuid,
      status: data.cached && data.layer_data ? 'completed' : data.status === 'pending' ? 'waiting' : 'processing',
      progress: data.cached && data.layer_data ? 100 : 0,
      pollCount: 0,
    });
    if (data.cached && data.layer_data) startWaitingJobs();
    else ensurePolling();
  } catch (error) {
    if (jobs[path] !== startingJob) return;
    const message = error.response?.data?.message || error.message || 'Could not start segmentation';
    updateJob(path, { status: 'failed', error: message });
    window.toastr?.error(message);
    startWaitingJobs();
  }
}

async function pollJobs() {
  if (polling) return;
  polling = true;
  try {
    await Promise.all(Object.values(jobs)
      .filter(job => ['waiting', 'processing'].includes(job.status) && job.jobUuid)
      .map(async job => {
        try {
          const { data } = await imageSegmentationApi.getJobStatus(job.jobUuid);
          if (jobs[job.imagePath]?.jobUuid !== job.jobUuid) return;
          if (data.status === 'completed') {
            updateJob(job.imagePath, { status: 'completed', progress: 100 });
          } else if (data.status === 'failed') {
            updateJob(job.imagePath, { status: 'failed', error: data.error_message || 'Segmentation failed' });
          } else if (data.status === 'pending') {
            updateJob(job.imagePath, { status: 'waiting', progress: 0 });
          } else {
            const pollCount = (job.pollCount || 0) + 1;
            const increment = Math.max(1, Math.floor(10 / Math.sqrt(pollCount)));
            updateJob(job.imagePath, {
              status: 'processing',
              pollCount,
              progress: Math.min(90, (job.progress || 0) + increment),
            });
          }
        } catch (error) {
          if (jobs[job.imagePath]?.jobUuid !== job.jobUuid) return;
          if ([403, 404].includes(error.response?.status)) {
            updateJob(job.imagePath, { status: 'failed', error: 'Segmentation job is unavailable' });
          } else if (error.response?.status) {
            updateJob(job.imagePath, { status: 'failed', error: error.response.data?.message || 'Could not check segmentation progress' });
          }
        }
      }));
    startWaitingJobs();
    if (!Object.values(jobs).some(job => ['waiting', 'processing'].includes(job.status)) && pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  } finally {
    polling = false;
  }
}

function ensurePolling() {
  if (!pollTimer) pollTimer = setInterval(pollJobs, POLL_INTERVAL);
}

Object.values(jobs).forEach(job => {
  if (job.status === 'starting') updateJob(job.imagePath, { status: 'queued' });
});
startWaitingJobs();
if (Object.values(jobs).some(job => ['waiting', 'processing'].includes(job.status))) ensurePolling();

export function startPsdImageSegmentation(imagePath, customerId = 0, psdFilePath = '') {
  const existing = jobs[imagePath];
  if (existing && existing.status !== 'failed') return;

  updateJob(imagePath, {
    imagePath,
    psdFilePath,
    customerId,
    jobUuid: null,
    status: 'queued',
    progress: 0,
    pollCount: 0,
    error: '',
  });
  startWaitingJobs();
}

export function clearPsdImageSegmentationJob(imagePath) {
  if (!jobs[imagePath]) return;
  Vue.delete(jobs, imagePath);
  saveJobs();
  startWaitingJobs();
}

export function clearPsdImageSegmentationJobs(psdFilePath) {
  if (!psdFilePath) return;
  Object.entries(jobs).forEach(([path, job]) => {
    if (job.psdFilePath === psdFilePath) Vue.delete(jobs, path);
  });
  saveJobs();
  startWaitingJobs();
}
